import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation, useNavigationState } from '@react-navigation/native';

import { SocialCurrencyToggle } from '@/components/SocialCurrencyToggle';
import { SocialWalletBalanceButton } from '@/components/SocialWalletBalanceButton';
import { DesktopHeader, DesktopHeaderLink } from '@/feature/desktop-nav/components/DesktopHeader';
import { DesktopHoverOverlay } from '@/feature/desktop-nav/components/DesktopHoverOverlay';
import { useRaf } from '@/feature/invite-friends/hooks/use-raf';

import { CasinoDesktopSearchModal } from './CasinoDesktopSearchModal';

const useActiveCasinoRoute = () =>
    useNavigationState(state => {
        const tab = state.routes[state.index];

        if (tab.name !== 'CasinoLobby') {
            return tab.name;
        }

        const lobbyStack = tab.state;
        return lobbyStack?.routes[lobbyStack.index ?? lobbyStack.routes.length - 1]?.name ?? 'CasinoLobbyScreen';
    });

export const CasinoDesktopHeader = () => {
    const navigation = useNavigation();
    const { t } = useTranslation('desktop_nav');
    const activeRoute = useActiveCasinoRoute();
    const { isRafEnabled } = useRaf();

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const openSearch = useCallback(() => {
        if (navigation.isFocused()) {
            setIsSearchOpen(true);
        }
    }, [navigation]);
    const closeSearch = useCallback(() => setIsSearchOpen(false), []);

    const links: DesktopHeaderLink[] = [
        {
            label: t('lobby'),
            active: activeRoute === 'CasinoLobbyScreen',
            testID: 'desktop-nav-lobby',
            onPress: () =>
                navigation.navigate('CasinoHome', {
                    screen: 'CasinoLobby',
                    params: {
                        screen: 'CasinoLobbyScreen',
                        params: { category: 'lobby' },
                    },
                }),
        },
        {
            label: t('promos'),
            active: activeRoute === 'LobbyPromohub',
            testID: 'desktop-nav-promos',
            onPress: () =>
                navigation.navigate('CasinoHome', {
                    screen: 'CasinoLobby',
                    params: { screen: 'LobbyPromohub' },
                }),
        },
        ...(isRafEnabled
            ? [
                  {
                      label: t('refer_a_friend'),
                      testID: 'desktop-nav-refer-a-friend',
                      onPress: () => navigation.navigate('InviteFriends'),
                  },
              ]
            : []),
    ];

    return (
        <>
            <DesktopHeader
                links={links}
                onSearchPress={openSearch}
                actions={
                    <>
                        <DesktopHoverOverlay borderRadius={20}>
                            <SocialWalletBalanceButton />
                        </DesktopHoverOverlay>
                        <DesktopHoverOverlay>
                            <SocialCurrencyToggle />
                        </DesktopHoverOverlay>
                    </>
                }
            />
            <CasinoDesktopSearchModal visible={isSearchOpen} onClose={closeSearch} />
        </>
    );
};
